import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter, createRootRoute, createRoute, Outlet } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { z } from 'zod';
import { ToastProvider } from '@lancar-pos/ui';
import { DatabaseProvider } from './db';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from './features/auth';
import { OnboardingWizard, GoogleSheetsSetup } from './features/onboarding';
import { SettingsPage } from './features/settings';
import { POSTerminal } from './features/pos';
import { ProductsPage, ProductFormPage, CategoriesPage, CategoryFormPage, OrdersPage, OrderDetailPage, UnitsPage, UnitFormPage, SuppliersPage, SupplierFormPage, PurchaseOrdersPage, PurchaseOrderFormPage, PurchaseOrderDetailPage, StockAdjustmentsPage, StockAdjustmentFormPage } from './features/inventory';
import { DashboardPage } from './features/dashboard/DashboardPage';
import './i18n/i18n';
import './index.css';

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'SKIP_WAITING') {
      window.location.reload();
    }
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

const rootRoute = createRootRoute({
  component: () => (
    <ToastProvider>
      <Outlet />
    </ToastProvider>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: function Index() {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-primary-600 mb-4">LancarPOS</h1>
          <p className="text-gray-600 mb-8">Kasir Online Tanpa Internet</p>
          <a
            href="/app"
            className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Buka Aplikasi
          </a>
        </div>
      </div>
    );
  },
});

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: function AppLayout() {
    return (
      <ProtectedRoute requireLicense={true}>
        <POSTerminal />
      </ProtectedRoute>
    );
  },
});

const loginSearchSchema = z.object({
  redirect: z.string().optional().default('/app'),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/login',
  validateSearch: loginSearchSchema,
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/register',
  component: RegisterPage,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/forgot-password',
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/reset-password',
  component: ResetPasswordPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/settings',
  component: function Settings() {
    return (
      <ProtectedRoute requireLicense={false}>
        <SettingsPage />
      </ProtectedRoute>
    );
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/dashboard',
  component: function Dashboard() {
    return (
      <ProtectedRoute requireLicense={false}>
        <DashboardPage />
      </ProtectedRoute>
    );
  },
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/onboarding',
  component: function Onboarding() {
    return (
      <ProtectedRoute requireLicense={false} requireOnboarding={false}>
        <OnboardingWizard />
      </ProtectedRoute>
    );
  },
});

const sheetsSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/sheets-setup',
  component: function SheetsSetup() {
    return (
      <ProtectedRoute requireLicense={false} requireOnboarding={false}>
        <GoogleSheetsSetup />
      </ProtectedRoute>
    );
  },
});

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/products',
  component: function Products() {
    return (
      <ProtectedRoute requireLicense={false}>
        <ProductsPage />
      </ProtectedRoute>
    );
  },
});

const productFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/products/$productId',
  component: function ProductForm() {
    return (
      <ProtectedRoute requireLicense={false}>
        <ProductFormPage />
      </ProtectedRoute>
    );
  },
});

const categoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/categories',
  component: function Categories() {
    return (
      <ProtectedRoute requireLicense={false}>
        <CategoriesPage />
      </ProtectedRoute>
    );
  },
});

const categoryFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/categories/$categoryId',
  component: function CategoryForm() {
    return (
      <ProtectedRoute requireLicense={false}>
        <CategoryFormPage />
      </ProtectedRoute>
    );
  },
});

const unitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/units',
  component: function Units() {
    return (
      <ProtectedRoute requireLicense={false}>
        <UnitsPage />
      </ProtectedRoute>
    );
  },
});

const unitFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/units/$unitId',
  component: function UnitForm() {
    return (
      <ProtectedRoute requireLicense={false}>
        <UnitFormPage />
      </ProtectedRoute>
    );
  },
});

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/orders',
  component: function Orders() {
    return (
      <ProtectedRoute requireLicense={false}>
        <OrdersPage />
      </ProtectedRoute>
    );
  },
});

const orderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/orders/$orderId',
  component: function OrderDetail() {
    return (
      <ProtectedRoute requireLicense={false}>
        <OrderDetailPage />
      </ProtectedRoute>
    );
  },
});

const suppliersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/suppliers',
  component: function Suppliers() {
    return (
      <ProtectedRoute requireLicense={false}>
        <SuppliersPage />
      </ProtectedRoute>
    );
  },
});

const supplierFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/suppliers/$supplierId',
  component: function SupplierForm() {
    return (
      <ProtectedRoute requireLicense={false}>
        <SupplierFormPage />
      </ProtectedRoute>
    );
  },
});

const purchaseOrdersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/purchase-orders',
  component: function PurchaseOrders() {
    return (
      <ProtectedRoute requireLicense={false}>
        <PurchaseOrdersPage />
      </ProtectedRoute>
    );
  },
});

const purchaseOrderFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/purchase-orders/new',
  component: function PurchaseOrderForm() {
    return (
      <ProtectedRoute requireLicense={false}>
        <PurchaseOrderFormPage />
      </ProtectedRoute>
    );
  },
});

const purchaseOrderDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/purchase-orders/$orderId',
  component: function PurchaseOrderDetail() {
    return (
      <ProtectedRoute requireLicense={false}>
        <PurchaseOrderDetailPage />
      </ProtectedRoute>
    );
  },
});

const stockAdjustmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/stock-adjustments',
  component: function StockAdjustments() {
    return (
      <ProtectedRoute requireLicense={false}>
        <StockAdjustmentsPage />
      </ProtectedRoute>
    );
  },
});

const stockAdjustmentFormRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/inventory/stock-adjustments/$adjustmentId',
  component: function StockAdjustmentForm() {
    return (
      <ProtectedRoute requireLicense={false}>
        <StockAdjustmentFormPage />
      </ProtectedRoute>
    );
  },
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '*',
  component: function NotFound() {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-6xl font-bold text-primary-500 mb-4">404</h1>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Halaman Tidak Ditemukan
          </h2>
          <p className="text-gray-600 mb-8">
            Maaf, halaman yang Anda cari tidak ada.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            Kembali ke Kasir
          </a>
        </div>
      </div>
    );
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  appLayoutRoute,
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  settingsRoute,
  dashboardRoute,
  onboardingRoute,
  sheetsSetupRoute,
  productsRoute,
  productFormRoute,
  categoriesRoute,
  categoryFormRoute,
  unitsRoute,
  unitFormRoute,
  ordersRoute,
  orderDetailRoute,
  suppliersRoute,
  supplierFormRoute,
  purchaseOrdersRoute,
  purchaseOrderFormRoute,
  purchaseOrderDetailRoute,
  stockAdjustmentsRoute,
  stockAdjustmentFormRoute,
  notFoundRoute,
]);

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <DatabaseProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </DatabaseProvider>
    </StrictMode>
  );
}
